import type { CompanyUpdate, FinancialItem, InvestmentCompany } from '../types';
import {
  seedCompanyUpdates,
  seedFinancialItems,
  seedInvestmentCompanies,
} from '../data/seedData';

/** Legacy keys from the pre-Firestore localStorage implementation. */
export const LEGACY_LS_KEY_FINANCIAL = 'perfina.pe.financialItems.v1';
export const LEGACY_LS_KEY_COMPANIES = 'perfina.pe.investmentCompanies.v1';
export const LEGACY_LS_KEY_UPDATES = 'perfina.pe.companyUpdates.v1';

/** Prevents copying one browser profile’s localStorage into another Firebase account. */
export const WORKSPACE_OWNER_MARKER_KEY = 'perfina.pe.workspaceOwnerUid.v1';

function readJson<T>(key: string): T | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === '') return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function isNonEmptyArray(v: unknown): v is unknown[] {
  return Array.isArray(v) && v.length > 0;
}

/**
 * Reads legacy localStorage entries and returns initial workspace arrays.
 * Prefers stored data when present; otherwise seed data.
 */
export function readLegacyLocalWorkspace(): {
  financialItems: FinancialItem[];
  companies: InvestmentCompany[];
  updates: CompanyUpdate[];
} {
  const fin = readJson<FinancialItem[]>(LEGACY_LS_KEY_FINANCIAL);
  const co = readJson<InvestmentCompany[]>(LEGACY_LS_KEY_COMPANIES);
  const up = readJson<CompanyUpdate[]>(LEGACY_LS_KEY_UPDATES);

  return {
    financialItems: isNonEmptyArray(fin) ? fin : seedFinancialItems,
    companies: isNonEmptyArray(co) ? co : seedInvestmentCompanies,
    updates: isNonEmptyArray(up) ? up : seedCompanyUpdates,
  };
}

export function clearLegacyLocalWorkspaceKeys(): void {
  try {
    window.localStorage.removeItem(LEGACY_LS_KEY_FINANCIAL);
    window.localStorage.removeItem(LEGACY_LS_KEY_COMPANIES);
    window.localStorage.removeItem(LEGACY_LS_KEY_UPDATES);
  } catch {
    /* ignore */
  }
}

function readOwnerMarker(): string | null {
  try {
    return window.localStorage.getItem(WORKSPACE_OWNER_MARKER_KEY);
  } catch {
    return null;
  }
}

/**
 * When Firestore has no workspace doc yet: if this browser was last used by a
 * different Firebase user, use seed data only. Otherwise prefer legacy keys, then seeds.
 */
export function resolveBootstrapWorkspace(userId: string): {
  financialItems: FinancialItem[];
  companies: InvestmentCompany[];
  updates: CompanyUpdate[];
} {
  const marker = readOwnerMarker();
  if (marker && marker !== userId) {
    return {
      financialItems: seedFinancialItems,
      companies: seedInvestmentCompanies,
      updates: seedCompanyUpdates,
    };
  }
  return readLegacyLocalWorkspace();
}

export function markWorkspaceOwner(userId: string): void {
  try {
    window.localStorage.setItem(WORKSPACE_OWNER_MARKER_KEY, userId);
  } catch {
    /* ignore */
  }
}
